/*
 *   File : list.jsx
 *   Author : https://evoqins.com
 *   Description : Revenue & Payouts list page
 *   Integrations : revenue-payout/list, revenue-payout/overview, revenue-payout/payout-overview, revenue-payout/download, revenue-payout/mark-as-paid
 *   Version : 1.5.0
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  setPageTitle,
  setSearchEnabled,
  setPlaceHolder,
  clearSearchQuery,
  setSearchOnEnter,
} from "../../Redux/Ui/ui.slice";
import { SummaryCard } from "../../Components/Cards";
import { Tabs } from "../../Components/Navigation";
import { DataTableComponent } from "../../Components/DataTable";
import { PrimaryButton } from "../../Components/Buttons";
import { MarkAsPaidModal, SuccessModal } from "../../Components/Modals";
import { Loader, EmptyScreen } from "../../Components/Other";
import { APIService } from "../../Service";

const MAIN_TABS = [
  { id: 0, name: "Revenue" },
  { id: 1, name: "Commission" },
  { id: 2, name: "Payouts" },
  { id: 3, name: "GST Invoices" },
];

const PAYOUT_SUB_TABS = [
  { id: 0, name: "Pending Payouts" },
  { id: 1, name: "Payout History" },
];

// API type: 1 = Revenue, 2 = Commission, 3 = Payout, 4 = GST invoice
const TAB_TO_API_TYPE = {
  0: 1,
  1: 2,
  2: 3,
  3: 4,
};

const REVENUE_COLUMNS = [
  {
    name: "Name",
    selector: (row) => row.name,
    wrap: true,
    width: "220px",
  },
  {
    name: "Gross Revenue",
    selector: (row) => row.gross_revenue_text,
    width: "160px",
  },
  {
    name: "Commission",
    selector: (row) => row.commission_text,
    width: "140px",
  },
  {
    name: "Net Revenue",
    selector: (row) => row.net_revenue_text,
    width: "140px",
  },
  {
    name: "Clients",
    selector: (row) => row.clients,
    width: "120px",
  },
];

const COMMISSION_COLUMNS = [
  {
    name: "Name",
    selector: (row) => row.name,
    wrap: true,
    width: "220px",
  },
  {
    name: "Period",
    selector: (row) => row.period,
    width: "140px",
  },
  {
    name: "Revenue",
    selector: (row) => row.revenue_text,
    width: "140px",
  },
  {
    name: "Commission %",
    selector: (row) => row.commission_percentage_text,
    width: "140px",
  },
  {
    name: "Commission Amount",
    selector: (row) => row.commission_amount_text,
    width: "180px",
  },
];

const GST_COLUMNS = [
  {
    name: "Invoice Number",
    selector: (row) => row.invoice_number,
    width: "180px",
  },
  {
    name: "Invoice Date",
    selector: (row) => row.invoice_date,
    width: "160px",
  },
  {
    name: "Taxable Amount",
    selector: (row) => row.taxable_amount_text,
    width: "160px",
  },
  {
    name: "GST Amount",
    selector: (row) => row.gst_amount_text,
    width: "140px",
  },
  {
    name: "Total Amount (₹)",
    selector: (row) => row.total_amount_text,
    width: "160px",
  },
  {
    name: "Action",
    width: "140px",
    cell: () => (
      <PrimaryButton
        ClassName="bg-transparent fs-12 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
        name="Download"
        onPress={() => {}}
      />
    ),
  },
];

export default function RevenueAndReportList() {
  const dispatch = useDispatch();
  const searchQuery = useSelector((state) => state.ui.searchQuery);

  const [activeTab, setActiveTab] = useState(0);
  const [payoutSubTab, setPayoutSubTab] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loader, setLoader] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);

  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoader, setOverviewLoader] = useState(true);
  const [overviewEmpty, setOverviewEmpty] = useState(false);

  const [payoutOverviewData, setPayoutOverviewData] = useState(null);
  const [payoutOverviewLoader, setPayoutOverviewLoader] = useState(false);
  const [payoutOverviewEmpty, setPayoutOverviewEmpty] = useState(false);
  const payoutOverviewFetchedRef = useRef(false);

  const [selectedPayout, setSelectedPayout] = useState(null);
  const [markAsPaidOpen, setMarkAsPaidOpen] = useState(false);
  const [markAsPaidLoading, setMarkAsPaidLoading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const listFetchIdRef = useRef(0);
  const listFetchDepsRef = useRef(null);
  const prevSearchRef = useRef(searchQuery);

  const _resetTableForFetch = () => {
    setLoader(true);
    setTableData([]);
    setIsEmpty(false);
    setTotalRows(0);
  };

  useEffect(() => {
    dispatch(setPageTitle(["Revenue & Payouts"]));
    dispatch(setSearchEnabled(true));
    dispatch(setSearchOnEnter(true));
    dispatch(setPlaceHolder("Search"));
    return () => {
      dispatch(setSearchOnEnter(false));
      dispatch(clearSearchQuery());
    };
  }, [dispatch]);

  useEffect(() => {
    setAppliedSearch(searchQuery);
    if (prevSearchRef.current === searchQuery) return;
    prevSearchRef.current = searchQuery;
    _resetTableForFetch();
    setPage(1);
  }, [searchQuery]);

  const _getOverview = () => {
    setOverviewLoader(true);
    setOverviewEmpty(false);

    APIService(false, "revenue-payout/overview")
      .then((res) => {
        if (res?.status_code === 200 && res?.data) {
          setOverviewData(res.data);
          setOverviewEmpty(false);
        } else {
          setOverviewData(null);
          setOverviewEmpty(true);
        }
      })
      .catch(() => {
        setOverviewData(null);
        setOverviewEmpty(true);
      })
      .finally(() => setOverviewLoader(false));
  };

  const _getPayoutOverview = () => {
    setPayoutOverviewLoader(true);
    setPayoutOverviewEmpty(false);

    APIService(false, "revenue-payout/payout-overview")
      .then((res) => {
        if (res?.status_code === 200 && res?.data) {
          setPayoutOverviewData(res.data);
          setPayoutOverviewEmpty(false);
        } else {
          setPayoutOverviewData(null);
          setPayoutOverviewEmpty(true);
        }
      })
      .catch(() => {
        setPayoutOverviewData(null);
        setPayoutOverviewEmpty(true);
      })
      .finally(() => setPayoutOverviewLoader(false));
  };

  useEffect(() => {
    _getOverview();
  }, []);

  // Fetch payout overview when Payouts tab is opened (once, then reuse)
  useEffect(() => {
    if (activeTab !== 2) return;
    if (payoutOverviewFetchedRef.current) return;
    payoutOverviewFetchedRef.current = true;
    _getPayoutOverview();
  }, [activeTab]);

  useEffect(() => {
    const depsKey = JSON.stringify({
      activeTab,
      payoutSubTab,
      page,
      perPage,
      appliedSearch,
    });
    if (listFetchDepsRef.current === depsKey) return;
    listFetchDepsRef.current = depsKey;
    _getList();
  }, [activeTab, payoutSubTab, page, perPage, appliedSearch]);

  const _openMarkAsPaid = (row) => {
    setSelectedPayout({
      ...row,
      partner: row.name,
      payout_period: row.period,
      payout_period_full: row.period,
      gross_commission_amount: row.gross_commission_text,
      tds_amount: row.tds_text,
      net_payout: row.net_payout_text || null,
    });
    setMarkAsPaidOpen(true);
  };

  const _handleMarkAsPaidConfirm = ({ transaction_id, payout_date } = {}) => {
    const payoutId = selectedPayout?.payout_id ?? selectedPayout?.id;
    if (!payoutId || !transaction_id) return;

    setMarkAsPaidLoading(true);
    APIService(true, "revenue-payout/mark-as-paid", {
      payout_id: payoutId,
      transaction_id,
      payout_date: payout_date || null,
    })
      .then((response) => {
        if (response?.status_code === 200) {
          setMarkAsPaidOpen(false);
          setSuccessModalOpen(true);
        } else {
          toast.dismiss();
          toast.error(response?.message || "Failed to mark as paid", {
            type: "error",
            className: "e-toast",
          });
        }
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Failed to mark as paid", {
          type: "error",
          className: "e-toast",
        });
      })
      .finally(() => {
        setMarkAsPaidLoading(false);
      });
  };

  const _downloadReport = () => {
    if (isEmpty || downloadLoading) return;
    setDownloadLoading(true);

    const payload = {
      type: TAB_TO_API_TYPE[activeTab] ?? 1,
      start_date: null,
      end_date: null,
    };

    if (activeTab === 2) {
      payload.payout_type = payoutSubTab === 0 ? 1 : 2;
    }

    APIService(true, "revenue-payout/download", payload)
      .then((response) => {
        if (response?.status_code === 200 && response.data?.url) {
          const link = document.createElement("a");
          link.href = response.data.url;
          link.download = ``;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("File downloaded successfully", {
            type: "success",
            className: "e-toast",
          });
        } else {
          toast.dismiss();
          toast.error(response?.message || "Failed to download report", {
            type: "error",
            className: "e-toast",
          });
        }
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Failed to download report", {
          type: "error",
          className: "e-toast",
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const _onTabChange = (tabId) => {
    if (tabId === activeTab) return;
    _resetTableForFetch();
    setActiveTab(tabId);
    setPayoutSubTab(0);
    setPage(1);
  };

  const _onPayoutSubTabChange = (tabId) => {
    if (tabId === payoutSubTab) return;
    _resetTableForFetch();
    setPayoutSubTab(tabId);
    setPage(1);
  };

  const pendingPayoutColumns = useMemo(
    () => [
      {
        name: "Partner",
        selector: (row) => row.name,
        wrap: true,
        width: "180px",
      },
      {
        name: "Payout Period",
        selector: (row) => row.period,
        width: "150px",
      },
      {
        name: "Gross Commission",
        selector: (row) => row.gross_commission_text,
        width: "170px",
      },
      {
        name: "TDS",
        selector: (row) => row.tds_text,
        width: "120px",
      },
      {
        name: "Due Date",
        selector: (row) => row.due_date,
        width: "150px",
      },
      {
        name: "Action",
        width: "150px",
        cell: (row) => (
          <PrimaryButton
            ClassName="bg-transparent fs-12 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
            name="Mark as Paid"
            onPress={() => _openMarkAsPaid(row)}
          />
        ),
      },
    ],
    [],
  );

  const payoutHistoryColumns = useMemo(
    () => [
      {
        name: "Partner",
        selector: (row) => row.name,
        wrap: true,
        width: "180px",
      },
      {
        name: "Payout Period",
        selector: (row) => row.period,
        width: "150px",
      },
      {
        name: "Gross Commission",
        selector: (row) => row.gross_commission_text,
        width: "170px",
      },
      {
        name: "TDS",
        selector: (row) => row.tds_text,
        width: "120px",
      },
      {
        name: "Paid On",
        selector: (row) => row.payout_date,
        width: "150px",
      },
    ],
    [],
  );

  const tableColumns = useMemo(() => {
    switch (activeTab) {
      case 1:
        return COMMISSION_COLUMNS;
      case 2:
        return payoutSubTab === 0
          ? pendingPayoutColumns
          : payoutHistoryColumns;
      case 3:
        return GST_COLUMNS;
      default:
        return REVENUE_COLUMNS;
    }
  }, [activeTab, payoutSubTab, pendingPayoutColumns, payoutHistoryColumns]);

  const emptyMessage = useMemo(() => {
    switch (activeTab) {
      case 1:
        return "No Commission Data Found";
      case 2:
        return payoutSubTab === 0
          ? "No Pending Payouts Found"
          : "No Payout History Found";
      case 3:
        return "No GST Invoices Found";
      default:
        return "No Revenue Data Found";
    }
  }, [activeTab, payoutSubTab]);

  const _getList = () => {
    const fetchId = ++listFetchIdRef.current;
    setLoader(true);

    const type = TAB_TO_API_TYPE[activeTab] ?? 1;
    const payload = {
      type,
      page_num: page,
      limit: perPage,
      query: appliedSearch || "",
      start_date: null,
      end_date: null,
    };

    if (type === 3) {
      // 1 = pending, 2 = history
      payload.payout_type = payoutSubTab === 0 ? 1 : 2;
    }

    APIService(true, "revenue-payout/list", payload).then((res) => {
        if (fetchId !== listFetchIdRef.current) return;
        if (res?.status_code === 200 && Array.isArray(res?.data?.data)) {
          setTableData(res.data.data);
          setTotalRows(res.data.total_records ?? 0);
          setIsEmpty(res.data.data.length === 0);
        } else {
          setTableData([]);
          setTotalRows(0);
          setIsEmpty(true);
        }
      })
      .catch(() => {
        if (fetchId !== listFetchIdRef.current) return;
        setTableData([]);
        setTotalRows(0);
        setIsEmpty(true);
      })
      .finally(() => {
        if (fetchId === listFetchIdRef.current) setLoader(false);
      });
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column rg-24" style={{ paddingTop: 72 }}>
          {/* Overview — padding 24, title→cards 20, cards gap 24 */}
          <div className="e-glass-card rounded-16 p-24 bg-transparent">
            <div className="d-flex flex-column rg-20">
              <h3 className="mb-0 fw-500 fs-14 lh-20 text-black">Overview</h3>
              {overviewLoader ? (
                <Loader />
              ) : overviewEmpty || !overviewData ? (
                <EmptyScreen message="No Overview Data Found" />
              ) : (
                <div className="row row-cols-lg-4 row-cols-md-2 row-cols-1 rg-24">
                  <div className="col">
                    <SummaryCard
                      title="Total Revenue"
                      description={overviewData.total_revenue_text ?? "—"}
                      value={overviewData.revenue_growth_text}
                      icon={
                        (overviewData.revenue_growth ?? 0) >= 0
                          ? "upward-swiggle"
                          : "downward-swiggle"
                      }
                      valueClassName={
                        (overviewData.revenue_growth ?? 0) >= 0
                          ? "text-medium-sea-green"
                          : "text-copper-red"
                      }
                    />
                  </div>
                  <div className="col">
                    <SummaryCard
                      title="Total Commission"
                      description={overviewData.total_commission_text ?? "—"}
                      value={overviewData.commission_growth_text}
                      icon={
                        (overviewData.commission_growth ?? 0) >= 0
                          ? "upward-swiggle"
                          : "downward-swiggle"
                      }
                      valueClassName={
                        (overviewData.commission_growth ?? 0) >= 0
                          ? "text-medium-sea-green"
                          : "text-copper-red"
                      }
                    />
                  </div>
                  <div className="col">
                    <SummaryCard
                      title="Total Payouts"
                      description={overviewData.total_payouts_text ?? "—"}
                      value={overviewData.payout_growth_text}
                      icon={
                        (overviewData.payout_growth ?? 0) >= 0
                          ? "upward-swiggle"
                          : "downward-swiggle"
                      }
                      valueClassName={
                        (overviewData.payout_growth ?? 0) >= 0
                          ? "text-medium-sea-green"
                          : "text-copper-red"
                      }
                    />
                  </div>
                  <div className="col">
                    <SummaryCard
                      title="Total TDS Deducted"
                      description={
                        overviewData.total_tds_deducted_text ?? "—"
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs section — padding 24; children gap 16 */}
          <div className="e-glass-card rounded-16 d-flex flex-column bg-transparent p-24 gap-3">
            <Tabs
              data={MAIN_TABS}
              defaultActive={0}
              onChange={_onTabChange}
            />

            {/* Payouts overview — padding 24, title→cards 20, cards gap 24 */}
            {activeTab === 2 && (
              <div className="e-glass-card rounded-16 p-24 bg-transparent">
                <div className="d-flex flex-column rg-20">
                  <h3 className="mb-0 fw-500 fs-14 lh-20 text-black">
                    Overview
                  </h3>
                  {payoutOverviewLoader ? (
                    <Loader />
                  ) : payoutOverviewEmpty || !payoutOverviewData ? (
                    <EmptyScreen message="No Payout Overview Data Found" />
                  ) : (
                    <div className="row row-cols-lg-4 row-cols-md-4 row-cols-2 rg-24">
                      <div className="col">
                        <SummaryCard
                          title="Total Generated"
                          description={
                            payoutOverviewData.total_generated_text ?? "—"
                          }
                        />
                      </div>
                      <div className="col">
                        <SummaryCard
                          title="Paid"
                          description={payoutOverviewData.paid_text ?? "—"}
                        />
                      </div>
                      <div className="col">
                        <SummaryCard
                          title="Pending"
                          description={payoutOverviewData.pending_text ?? "—"}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Table wrapper — padding 24; download→table gap 16 */}
            <div className="e-glass-card rounded-16 d-flex flex-column bg-transparent gap-3">
              {activeTab === 2 ? (
                <div className="d-flex justify-content-between align-items-center flex-wrap row-gap-3 px-4 pt-4">
                  <Tabs
                    data={PAYOUT_SUB_TABS}
                    defaultActive={payoutSubTab}
                    labelStyle="fw-500 fs-16 lh-20"
                    onChange={_onPayoutSubTabChange}
                  />
                  <PrimaryButton
                    ClassName="e-download-btn rounded-16 bg-midnight-blue lh-24 py-2 px-4 text-white fs-16 fw-400"
                    name={downloadLoading ? "Downloading..." : "Download"}
                    loading={downloadLoading}
                    disabled={downloadLoading || isEmpty || loader}
                    onPress={_downloadReport}
                  />
                </div>
              ) : (
                <div className="d-flex justify-content-end pe-4 pt-4">
                  <PrimaryButton
                    ClassName="e-download-btn rounded-16 bg-midnight-blue lh-24 py-2 px-4 text-white fs-16 fw-400"
                    name={downloadLoading ? "Downloading..." : "Download"}
                    loading={downloadLoading}
                    disabled={downloadLoading || isEmpty || loader}
                    onPress={_downloadReport}
                  />
                </div>
              )}

              {loader ? (
                <Loader />
              ) : isEmpty ? (
                <EmptyScreen message={emptyMessage} />
              ) : (
                <DataTableComponent
                  columns={tableColumns}
                  data={tableData}
                  pagination={true}
                  paginationServer={true}
                  currentPage={page}
                  paginationTotalRows={totalRows}
                  paginationPerPage={perPage}
                  onChangePage={(p) => setPage(p)}
                  onChangeRowsPerPage={(newPerPage) => {
                    setPage(1);
                    setPerPage(newPerPage);
                  }}
                  className="e-table-basic e-revenue-payouts-table"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <MarkAsPaidModal
        open={markAsPaidOpen}
        payout={selectedPayout}
        loading={markAsPaidLoading}
        onCancel={() => {
          setMarkAsPaidOpen(false);
          setSelectedPayout(null);
        }}
        onConfirm={_handleMarkAsPaidConfirm}
      />
      <SuccessModal
        open={successModalOpen}
        title="Payout Marked as Paid"
        message="The payout has been successfully processed and recorded in the payout history."
        onConfirm={() => {
          setSuccessModalOpen(false);
          setSelectedPayout(null);
          _resetTableForFetch();
          setPage(1);
          setPayoutSubTab(1); // switch to Payout History
          _getPayoutOverview();
        }}
      />
    </div>
  );
}
